import { useMemo } from 'react';
import { useTheme } from '@mui/material';
import { DataGrid, DataGridProps, GridColDef } from '@mui/x-data-grid';
import { withSerialNo, SERIAL_NO_FIELD } from './gridColumns';
import ResponsiveGridContainer from './ResponsiveGridContainer';
import { useResponsiveGrid } from '../../hooks/useResponsiveGrid';
import { useI18n } from '../../i18n/I18nProvider';
import { translateUiText } from '../../i18n/translateUiText';
import { adminDataGridSx } from './adminDataGridSx';
import AdminGridEmpty from './AdminGridEmpty';

type SerialNoDataGridProps = DataGridProps;

export default function SerialNoDataGrid({
  columns = [],
  columnVisibilityModel: externalVisibility,
  initialState,
  sx,
  ...props
}: SerialNoDataGridProps) {
  const theme = useTheme();
  const { t } = useI18n();

  const gridColumns = useMemo(() => {
    const mapped = (columns as GridColDef[]).map((col) => ({
      ...col,
      headerName: col.headerName ? translateUiText(col.headerName, t) : col.headerName,
    }));
    return withSerialNo(mapped).map((col) =>
      col.field === SERIAL_NO_FIELD
        ? { ...col, headerName: translateUiText('S.No', t) }
        : col,
    );
  }, [columns, t]);

  const { columnVisibilityModel, density, pageSize, responsiveColumns } = useResponsiveGrid(
    gridColumns,
    externalVisibility,
  );

  return (
    <ResponsiveGridContainer>
      <DataGrid
        pageSizeOptions={[10, 15, 25, 50]}
        disableRowSelectionOnClick
        columnHeaderHeight={48}
        rowHeight={52}
        slots={{
          noRowsOverlay: () => (
            <AdminGridEmpty
              title={translateUiText('No data found', t)}
              subtitle={translateUiText('Nothing to show yet', t)}
            />
          ),
        }}
        initialState={{
          pagination: { paginationModel: { pageSize } },
          ...initialState,
        }}
        {...props}
        columns={responsiveColumns}
        density={density}
        columnVisibilityModel={columnVisibilityModel}
        sx={[adminDataGridSx(theme), sx] as DataGridProps['sx']}
      />
    </ResponsiveGridContainer>
  );
}
